import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronUp, History, X } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { API, api } from "@/lib/api";
import { openResumeSong } from "@/components/SongWizard";

const CID_KEY = "episnya_client_id";
const getCid = () => {
  try { return localStorage.getItem(CID_KEY) || ""; } catch { return ""; }
};

const track = (source, detail) => {
  try {
    api.post("/web/click", detail ? { source, detail } : { source }).catch(() => {});
  } catch { /* noop */ }
};

const ST_KEY = {
  unpaid: "st_unpaid",
  created: "st_pending", screenshot_wait: "st_pending", pending: "st_pending",
  paid: "st_generating", in_progress: "st_generating", rendering: "st_generating",
  done: "st_done", completed: "st_done",
  failed: "st_rejected", expired: "st_expired", rejected: "st_rejected",
};
const ST_CLS = {
  unpaid: "text-[#FFD400] border-[#FFD400]/30 bg-[#FFD400]/10",
  created: "text-[#8CCBFF] border-[#2BA8FF]/40 bg-[#2BA8FF]/10",
  screenshot_wait: "text-[#8CCBFF] border-[#2BA8FF]/40 bg-[#2BA8FF]/10",
  pending: "text-[#8CCBFF] border-[#2BA8FF]/40 bg-[#2BA8FF]/10",
  paid: "text-[#2BA8FF] border-[#2BA8FF]/50 bg-[#2BA8FF]/15",
  in_progress: "text-[#2BA8FF] border-[#2BA8FF]/50 bg-[#2BA8FF]/15",
  rendering: "text-[#2BA8FF] border-[#2BA8FF]/50 bg-[#2BA8FF]/15",
  done: "text-[#10B981] border-[#10B981]/40 bg-[#10B981]/10",
  completed: "text-[#10B981] border-[#10B981]/40 bg-[#10B981]/10",
  failed: "text-[#FF8A8A] border-[#FF8A8A]/30 bg-[#FF8A8A]/10",
  expired: "text-[#647089] border-white/10 bg-white/5",
  rejected: "text-[#FF8A8A] border-[#FF8A8A]/30 bg-[#FF8A8A]/10",
};
const ACTIVE_ST = new Set(["unpaid", "created", "screenshot_wait", "pending",
                           "paid", "in_progress", "rendering"]);

export default function LastOrderWidget() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    const cid = getCid();
    if (!cid) return;
    api.get("/public/landing/state", { params: { client_id: cid } })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (open) load(); }, [open, load]);

  // Пока заказ в работе — опрашиваем статус каждые 10 секунд (обновляется сам).
  useEffect(() => {
    const active = items.some((it) => ACTIVE_ST.has(it.status));
    if (!active) return undefined;
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [items, load]);

  // Конструктор закрылся — сразу перепроверяем, есть ли что показать (без F5).
  useEffect(() => {
    const onRefresh = () => load();
    window.addEventListener("episnya:widget-refresh", onRefresh);
    return () => window.removeEventListener("episnya:widget-refresh", onRefresh);
  }, [load]);

  // SSE: мгновенные обновления статусов (опрос остаётся страховкой).
  useEffect(() => {
    const cid = getCid();
    if (!cid) return undefined;
    let es;
    try {
      es = new EventSource(`${API}/public/landing/stream?client_id=${encodeURIComponent(cid)}`);
    } catch { return undefined; }
    es.onmessage = () => load();
    return () => es.close();
  }, [load]);

  // Сообщаем странице, что виджет виден — тогда всплывающая кнопка скрывается.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("episnya:widget", { detail: items.length > 0 }));
    return () => window.dispatchEvent(new CustomEvent("episnya:widget", { detail: false }));
  }, [items.length]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!items.length) return null;

  const last = items[0];
  const st = (s) => t[ST_KEY[s]] || s;

  const resume = (it) => {
    track("widget_resume", `${it.genre_label}${it.number ? ` · #${it.number}` : ""}`);
    setOpen(false);
    openResumeSong({
      mode: it.mode || "studio",
      genre: it.genre,
      voice: it.voice,
      description: it.description,
      delivery: it.delivery,
      contact: it.contact,
      step: it.progress === "payment" ? 3 : 2,
      event: it.event,
      event_text: it.event_text,
      mood: it.mood,
      mood_text: it.mood_text,
      song_language: it.song_language,
      genre_mix: it.genre_mix,
      genre_text: it.genre_text,
    });
  };

  const openListen = (it) => {
    track("widget_song", `#${it.number || ""}`);
    setOpen(false);
    const url = it.listen_url.startsWith("http")
      ? it.listen_url
      : `${window.location.origin}${it.listen_url}`;
    window.open(url, "_blank");
  };

  return (
    <>
      <button data-testid="last-order-widget"
        onClick={() => { track("widget_click", st(last.status)); setOpen(true); }}
        className="fixed left-1/2 -translate-x-1/2 z-[60] max-w-[calc(100vw-1.5rem)] rounded-2xl border border-white/15 bg-white/[0.07] backdrop-blur-xl pl-3.5 pr-8 py-2.5 text-left shadow-2xl hover:bg-white/[0.11] transition-colors"
        style={{ bottom: "max(14px, env(safe-area-inset-bottom))" }}>
        {/* просто ⚠️ сбоку, с наклоном как у цен в карточках конструктора */}
        <span className="absolute -top-2.5 -right-1.5 rotate-[8deg] text-base leading-none"
          style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.45))" }}>
          ⚠️
        </span>
        <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[#9FB0C7]">
          <History size={11} className="text-[#2BA8FF] shrink-0" />
          <span className="truncate">{t.last_creation}</span>
        </span>
        <span className="mt-1 flex items-center gap-1.5 min-w-0">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${ST_CLS[last.status] || ""}`}>
            {st(last.status)}
          </span>
          {last.number && (
            <span className="font-mono text-[10px] text-[#647089] whitespace-nowrap">#{last.number}</span>
          )}
          <span className="ml-auto flex items-center shrink-0">
            <ChevronUp size={13} className="text-[#2BA8FF]" />
          </span>
        </span>
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[88] bg-[#060A14]/70 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0"
          onClick={() => setOpen(false)} role="dialog" aria-modal="true">
          <div className="w-full max-w-lg max-h-[80vh] overflow-y-auto no-scrollbar rounded-3xl border border-white/12 bg-white/[0.08] backdrop-blur-2xl p-5"
            style={{ boxShadow: "0 24px 80px rgba(0,0,0,0.55)" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-4">
              <h3 className="font-display text-lg font-extrabold uppercase">{t.last_orders_title}</h3>
              <button onClick={() => setOpen(false)} aria-label={t.done_close}
                className="grid place-items-center w-8 h-8 rounded-full border border-white/15 text-[#9FB0C7] hover:text-white hover:border-white/30 transition-colors">
                <X size={16} />
              </button>
            </div>
            <div className="mt-4 space-y-2.5">
              {items.map((it) => {
                const resumable = it.status === "unpaid";
                const listenable = !!it.listen_url;
                const clickable = resumable || listenable;
                const onCard = resumable ? () => resume(it) : (listenable ? () => openListen(it) : undefined);
                return (
                  <div key={it.id}
                    role={clickable ? "button" : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onClick={onCard}
                    onKeyDown={clickable ? (e) => {
                      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onCard(); }
                    } : undefined}
                    className={`rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3 ${clickable
                      ? "cursor-pointer hover:bg-white/[0.09] transition-colors" : ""}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${ST_CLS[it.status] || ""}`}>
                        {st(it.status)}
                      </span>
                      {it.number && <span className="font-mono text-xs text-[#C6D3E6]">#{it.number}</span>}
                      <span className="text-[11px] text-[#647089] font-mono ml-auto">
                        {(it.created_at || "").slice(0, 10)}
                      </span>
                    </div>
                    <p className="mt-1.5 text-xs text-[#9FB0C7]">{(it.genre_mix || []).length ? "Мікс" : it.genre_label} · {it.voice_label}</p>
                    <p className="review-text mt-1 text-xs text-[#C6D3E6]">{it.description}</p>
                    {resumable && (
                      <span className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#8CCBFF]">
                        {t.last_continue} →
                      </span>
                    )}
                    {listenable && (
                      <span className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#10B981]">
                        🎧 {t.last_open_song} →
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
